// A finished task's camera footage (the job pages' "View task video"): ONE copy, used by the desktop
// Jobs pages (pages/JobsPage.js, pages/JobsDetailsPage.js; each had its own copy until 2026-10-09)
// and the phone job detail. The relay is the camera Pi (utils/cameraRelay.js), not jobq-api.
import { CAMERA_BASE_URL, clipStartMinute, fileUrl, fmtClock, getCfHeaders, rangeUrl } from "../../utils/cameraRelay";

const RELAY = () => ({ headers: getCfHeaders(), credentials: "include" });

/** GET /cameras/config ({station: camera count}); null when the relay can't be reached. */
export async function loadCameraConfig() {
  try {
    const res = await fetch(`${CAMERA_BASE_URL}/cameras/config`, RELAY());
    return res.ok ? await res.json() : null;
  } catch (e) {
    return null;
  }
}

/** How many cameras watch the task's station. */
export function taskCameraCount(task, cameraConfig) {
  return task && cameraConfig ? (cameraConfig[task.workstation] || 0) : 0;
}

/** Video is offered for a finished task (start + end known) at a station with cameras. */
export function canShowTaskVideo(task, cameraConfig) {
  return Boolean(task && task.status === "Completed" && task.start_time && task.end_time && taskCameraCount(task, cameraConfig) > 0);
}

/** Each camera's clips in the task's time range: {cameraIndex: [filename, ...]}; a camera that fails has []. */
export async function listTaskClips(task, camCount) {
  const fetches = [];
  for (let idx = 0; idx < camCount; idx++) {
    fetches.push(
      fetch(rangeUrl(task.workstation, idx, task.start_time, task.end_time), RELAY())
        .then((res) => (res.ok ? res.json() : { files: [] }))
        .then((data) => ({ idx, files: data.files || [] }))
        .catch(() => ({ idx, files: [] }))
    );
  }
  const all = {};
  (await Promise.all(fetches)).forEach(({ idx, files }) => { all[idx] = files; });
  return all;
}

/** The most clips any one camera has (the clip navigator's length). */
export function clipCount(clips) {
  return Object.values(clips || {}).reduce((mx, f) => Math.max(mx, f.length), 0);
}

/**
 * Every camera's clip number `clipIdx`, fetched (the relay sits behind Cloudflare Access, which
 * can't serve media range requests) and handed back as blob: URLs, {cameraIndex: url}. A camera
 * whose clip fails is left out. The caller revokes the URLs when done.
 */
export async function loadClipUrls(clips, clipIdx, station) {
  const urls = {};
  const fetches = [];
  for (const [camIdx, files] of Object.entries(clips || {})) {
    const file = files[clipIdx];
    if (!file) continue;
    fetches.push(
      fetch(fileUrl(station, camIdx, file), RELAY())
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load video (${res.status})`);
          return res.blob();
        })
        .then((blob) => { urls[camIdx] = URL.createObjectURL(blob); })
        .catch(() => { /* one camera failing is fine */ })
    );
  }
  await Promise.all(fetches);
  return urls;
}

/**
 * A clip filename as its clock time: "2026-10-07_14-00-00.mp4" -> "2:00:00 PM". The relay names
 * clips in the Pi's LOCAL time (Central), so the time is read as written, never converted
 * (camera_relay CLAUDE.md: "Do NOT assume filenames are UTC"; the job detail page used to treat
 * them as UTC and showed every clip 5-6 h early).
 */
export function clipTimeCT(filename) {
  const minute = clipStartMinute(filename);
  return minute === null ? filename : fmtClock(minute, { seconds: true });
}
